"use client"

import React, { useState, useEffect, useId, useMemo, useCallback, useRef, useImperativeHandle, useSyncExternalStore, startTransition } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Sentry from '@/lib/sentry-safe'
import { Search, X, ChevronDown } from 'lucide-react'
import { ViewSwitch } from '@/components/ui/tls/view-switch'
import { Input } from '@/components/ui/input'
import { type Category } from '@/lib/supabase'
import { fetchCategories } from '@/lib/api/client'
import { TESLA_FACTS } from '@/lib/tesla-facts'
import { CategoryOverflowRow } from '@/components/ui/tls/category-overflow-row'
import { writeSoundsUrl } from '@/app/sounds/write-sounds-url'
import { useStickyScrollReserve } from '@/hooks/useStickyScrollReserve'

interface SearchFiltersProps {
  onSearch: (filters: FilterState) => void
  initialFilters?: Partial<FilterState>
  categories?: Category[]
  categoryCounts?: Record<string, number>
  selectedCategory?: string
  onCategoryChange?: (category: string) => void
  /**
   * Opens the ONE filter sheet (FilterRail's) — the mobile half of the DS
   * FilterRow contract ("on mobile the same component becomes a Sheet trigger
   * with an applied-count badge"). Below `lg` this trigger replaces the
   * category pill row entirely; there is never a second category affordance.
   */
  onOpenCategorySheet?: () => void
  /**
   * Canonical full Tesla model name ("Model S", "Model 3", …) from the
   * FilterRail ↔ bar sync boundary (see app/sounds/use-filter-sync.ts). When
   * provided, keeps this bar's internal filters.model aligned with rail
   * changes so a later keystroke's debounced onSearch(filters) call doesn't
   * revert a rail-driven model change back to a stale value.
   */
  model?: string
  /** Editor's-picks toggle state, mirrored from the FilterRail (TES rail ↔ bar sync). */
  picksOnly?: boolean
  /**
   * The "Showing 12 of 1,400" position line. Rendered on the list-header row
   * this component owns, so the count and the sort control sit on one line
   * exactly as the DS Library template lays them out.
   */
  resultsSummary?: React.ReactNode
  onPicksOnlyChange?: (picksOnly: boolean) => void
  /**
   * Clears EVERY filter surface at once. Required whenever the page owns
   * filter state outside this bar (category/model/picks also live in the
   * filter sheet): clearing via the individual per-facet callbacks instead
   * re-broadcasts whichever facet the page had not cleared yet, which left
   * "Clear all" showing the old category's result set.
   */
  onClearFilters?: () => void
  className?: string
  ref?: React.Ref<SearchFiltersHandle>
}

export interface FilterState {
  query: string
  category: string
  model: string
  sortBy: 'created_at' | 'downloads' | 'title' | 'duration' | 'trending'
  sortOrder: 'asc' | 'desc'
}

const DEFAULT_FILTERS: FilterState = {
  query: '',
  category: 'All',
  model: '',
  sortBy: 'downloads',
  sortOrder: 'desc'
}

export const SORT_OPTIONS = [
  { value: 'downloads_desc', sortBy: 'downloads' as const, sortOrder: 'desc' as const, label: 'Most downloaded', short: 'Popular' },
  { value: 'trending_desc', sortBy: 'trending' as const, sortOrder: 'desc' as const, label: 'Trending this week', short: 'Trending' },
  { value: 'created_at_desc', sortBy: 'created_at' as const, sortOrder: 'desc' as const, label: 'Newest', short: 'Newest' },
  { value: 'title_asc', sortBy: 'title' as const, sortOrder: 'asc' as const, label: 'A → Z', short: 'A to Z' },
  { value: 'duration_asc', sortBy: 'duration' as const, sortOrder: 'asc' as const, label: 'Shortest', short: 'Shortest' },
  { value: 'duration_desc', sortBy: 'duration' as const, sortOrder: 'desc' as const, label: 'Longest', short: 'Longest' },
  { value: 'downloads_asc', sortBy: 'downloads' as const, sortOrder: 'asc' as const, label: 'Least downloaded', short: 'Least popular' },
  { value: 'created_at_asc', sortBy: 'created_at' as const, sortOrder: 'asc' as const, label: 'Oldest', short: 'Oldest' },
  { value: 'title_desc', sortBy: 'title' as const, sortOrder: 'desc' as const, label: 'Z → A', short: 'Z to A' },
]

/** The sorts the list header offers as views; the rest are in the "More" select. */
const VIEW_SORTS: readonly string[] = ['downloads_desc', 'trending_desc', 'created_at_desc', 'duration_asc']
const SORT_VIEWS = SORT_OPTIONS.filter(opt => VIEW_SORTS.includes(opt.value)).map(opt => ({ id: opt.value, label: opt.short }))

function readFiltersFromUrl(get: (key: string) => string | null): FilterState {
  const sortOption = SORT_OPTIONS.find(option => option.value === get('sort'))
  return {
    query: get('q') || get('search') || '',
    category: get('category') || 'All',
    model: get('model') || '',
    sortBy: sortOption?.sortBy ?? DEFAULT_FILTERS.sortBy,
    sortOrder: sortOption?.sortOrder ?? DEFAULT_FILTERS.sortOrder,
  }
}

function filtersMatch(left: FilterState, right: FilterState): boolean {
  return left.query === right.query &&
    left.category === right.category &&
    left.model === right.model &&
    left.sortBy === right.sortBy &&
    left.sortOrder === right.sortOrder
}

const activeFilterClassName =
  'border-[color:var(--tls-border-strong)] bg-[var(--tls-surface-hover)] text-[var(--tls-fg-strong)]'
const neutralFilterClassName =
  'border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]'
const activeDropdownOptionClassName =
  'bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-fg-strong)]'

/** Where an active ?q= or filter scrolls the page to: the results list header. */
export const RESULTS_ANCHOR_ID = 'sound-results'

const DESKTOP_QUERY = '(min-width: 64rem)'
function subscribeDesktop(onChange: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {}
  const mq = window.matchMedia(DESKTOP_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}
function getDesktopSnapshot() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches
}
function getDesktopServerSnapshot() {
  return false
}

export type SearchFiltersHandle = {
  focusSearch: () => void
}

// eslint-disable-next-line react-doctor/no-giant-component -- complex filter bar (search, categories, sort, model, mobile layout); splitting would require extensive prop drilling
// eslint-disable-next-line react-doctor/rerender-memo-with-default-value -- initialFilters/categoryCounts/className defaults are safe; this is not a memo'd component
// eslint-disable-next-line react-doctor/prefer-useReducer -- UI toggle state (dropdown open/close) is independent; combining into a reducer would obscure intent without benefit
export function SearchFilters({
  onSearch,
  initialFilters = {},
  categories: initialCategories,
  categoryCounts = {},
  selectedCategory,
  onCategoryChange,
  onOpenCategorySheet,
  model,
  picksOnly = false,
  onPicksOnlyChange,
  onClearFilters,
  resultsSummary,
  className = '',
  ref,
}: SearchFiltersProps) {
  // eslint-disable-next-line react-doctor/nextjs-no-use-search-params-without-suspense -- this component is always rendered inside a Suspense boundary by its parent (sounds page)
  const searchParams = useSearchParams()
  // Stable wrapper keeps URL-sync dependencies explicit without exposing the
  // mutable method binding to the effect.
  const searchParamsGet = useCallback((key: string) => searchParams.get(key), [searchParams])
  const urlFilters = useMemo(() => readFiltersFromUrl(searchParamsGet), [searchParamsGet])
  const urlFilterKey = JSON.stringify(urlFilters)
  const hasUrlFilterState = ['q', 'search', 'category', 'model', 'sort'].some(key => searchParams.has(key))

  // eslint-disable-next-line react-doctor/no-initialize-state, react-doctor/no-derived-state -- preserve non-URL seeds while URL filters take precedence
  const [filters, setFilters] = useState<FilterState>(() => ({
    ...DEFAULT_FILTERS,
    ...initialFilters,
    ...(hasUrlFilterState ? urlFilters : {}),
  }))

  const lastSyncedUrlFilterKeyRef = useRef(urlFilterKey)
  const skipNextFilterEffectRef = useRef(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  // The bar is sticky; reserve its height so focus and anchor jumps land below it.
  const stickyBarRef = useRef<HTMLElement>(null)
  useStickyScrollReserve(stickyBarRef)
  const [categories, setCategories] = useState<Category[]>(initialCategories ?? [])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsContainerRef = useRef<HTMLDivElement>(null)
  const suggestionItemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      searchInputRef.current?.focus()
    }
  }), [])
  const [panelOpen, setPanelOpen] = useState(false)
  const isDesktop = useSyncExternalStore(subscribeDesktop, getDesktopSnapshot, getDesktopServerSnapshot)
  const panelTriggerRef = useRef<HTMLButtonElement>(null)
  const uid = useId()
  const panelId = `${uid}-filters-panel`
  const onSearchRef = useRef(onSearch)
  const isInitialMountRef = useRef(true)

  const handleSuggestionKeyDown = useCallback((event: React.KeyboardEvent, index: number) => {
    const items = suggestionItemRefs.current.filter((el): el is HTMLButtonElement => el !== null)
    if (event.key === 'Escape') {
      event.preventDefault()
      setShowSuggestions(false)
      searchInputRef.current?.focus()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[(index + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (index === 0) {
        searchInputRef.current?.focus()
      } else {
        items[index - 1]?.focus()
      }
    }
  }, [])

  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  // Browser history changes the URL without remounting this component. Keep
  // its controls and the parent result state aligned with the restored URL.
  // eslint-disable-next-line react-doctor/no-adjust-state-on-prop-change -- URL navigation is an external state change
  useEffect(() => {
    if (lastSyncedUrlFilterKeyRef.current === urlFilterKey) return
    lastSyncedUrlFilterKeyRef.current = urlFilterKey

    if (filtersMatch(filters, urlFilters)) return

    skipNextFilterEffectRef.current = true
    setFilters(urlFilters)
    onSearchRef.current(urlFilters)
  }, [filters, urlFilterKey, urlFilters])

  // Keep internal filters.model aligned with the externally-synced `model`
  // prop (FilterRail changes). Without this, a rail model change only shows
  // up in `activeModel` for display — the next debounced onSearch(filters)
  // fire (e.g. from a search-box keystroke) would still send the *stale*
  // internal filters.model and silently revert the rail's change.
  useEffect(() => {
    if (model === undefined) return
    startTransition(() => {
      setFilters(prev => (prev.model === model ? prev : { ...prev, model }))
    })
  }, [model])

  // Same contract for category. The filter sheet writes category through
  // `selectedCategory`; without this the bar's internal filters.category kept
  // the stale value and the next debounced onSearch silently reverted the
  // sheet's choice (the exact bug the `model` effect above documents).
  useEffect(() => {
    if (selectedCategory === undefined) return
    startTransition(() => {
      setFilters(prev => (prev.category === selectedCategory ? prev : { ...prev, category: selectedCategory }))
    })
  }, [selectedCategory])

  useEffect(() => {
    if (initialCategories && initialCategories.length > 0) return
    const loadCategories = async () => {
      try {
        const data = await fetchCategories()
        setCategories(data)
      } catch (error) {
        Sentry.captureException(error, { tags: { context: 'SearchFilters', action: 'loadData' } })
      }
    }
    loadCategories()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateURL = useCallback((newFilters: FilterState) => {
    // Base on the *current* URL rather than rebuilding from scratch — this
    // bar only knows about q/category/model/sort, so a from-scratch build
    // would silently drop params owned by other writers (FilterRail's
    // ?picks=1, the pager's ?page=). A filter change here still means a
    // fresh result set, so ?page= is explicitly cleared (page 1).
    const params = new URLSearchParams(searchParams.toString())
    if (newFilters.query) params.set('q', newFilters.query)
    else params.delete('q')
    params.delete('search') // legacy alias, never re-emitted
    if (newFilters.category !== 'All') params.set('category', newFilters.category)
    else params.delete('category')
    if (newFilters.model) params.set('model', newFilters.model)
    else params.delete('model')

    const sortValue = `${newFilters.sortBy}_${newFilters.sortOrder}`
    if (sortValue !== 'downloads_desc') params.set('sort', sortValue)
    else params.delete('sort')

    params.delete('page')

    const queryString = params.toString()
    const newUrl = queryString ? `/sounds?${queryString}` : '/sounds'
    writeSoundsUrl(newUrl)
  }, [searchParams])

  // Held in a ref so the debounced URL-sync effect below can depend on `filters`
  // alone. `updateURL` closes over `searchParams`, so its identity changes on
  // every URL change — keeping it as an effect dependency made the effect fire
  // (and re-push a page-stripped URL) for URL changes this bar did not cause.
  const updateURLRef = useRef(updateURL)
  useEffect(() => {
    updateURLRef.current = updateURL
  }, [updateURL])

  const searchSuggestions = useMemo(() => {
    if (filters.query.length > 2) {
      return categories.filter(cat =>
        cat.name.toLowerCase().includes(filters.query.toLowerCase())
      ).slice(0, 5)
    }
    return []
  }, [filters.query, categories])

  const derivedShowSuggestions = searchSuggestions.length > 0 && filters.query.length > 2

  // Push the debounced filter state out to the parent + the URL. Deliberately
  // keyed on `filters` ONLY: `updateURL` strips `?page=`, so depending on its
  // (searchParams-derived) identity made *any* URL change re-run this effect and
  // 300ms later push `/sounds` with the pager's `?page=N` deleted. The pager
  // then mirrored its unchanged page back in, and the two writers ping-ponged
  // ~3x/second until Safari threw `SecurityError: Attempt to use
  // history.replaceState() more than 100 times per 30 seconds` (TES-1872).
  // eslint-disable-next-line react-doctor/prefer-use-effect-event -- useEffectEvent is not yet stable in this project's React version
  useEffect(() => {
    if (skipNextFilterEffectRef.current) {
      skipNextFilterEffectRef.current = false
      return
    }

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      onSearchRef.current(filters)
      return
    }

    const timeoutId = setTimeout(() => {
      onSearchRef.current(filters)
      updateURLRef.current(filters)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [filters])

  const handleFilterChange = useCallback(<K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [setFilters])

  const handleCategorySelect = useCallback((categoryName: string) => {
    setFilters(prev => ({ ...prev, category: categoryName }))
    onCategoryChange?.(categoryName)
  }, [setFilters, onCategoryChange])

  // The sheet-trigger badge counts *applied facets* — never available ones,
  // and never sort (sort is not a filter: it has its own always-visible
  // control on the list header row, per the DS Library template).
  const activeCategory = selectedCategory ?? filters.category
  const activeModel = model ?? filters.model

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (activeCategory !== 'All') count++
    if (activeModel) count++
    if (picksOnly) count++
    return count
  }, [activeCategory, activeModel, picksOnly])

  const filterCountBadge = activeFilterCount > 0 ? (
    <span className="flex size-4 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--tls-brand)] text-[0.6875rem] font-bold text-[var(--tls-brand-fg)]">
      {activeFilterCount}
    </span>
  ) : null

  const allCategoryCount = useMemo(
    () => Object.values(categoryCounts).reduce((sum, count) => sum + count, 0),
    [categoryCounts]
  )

  // Four sorts as views (the cohesion plan's ViewSwitch); the rest sit in a
  // select beside them. Either control writes the same filter state.
  const activeSort = SORT_OPTIONS.find(
    opt => opt.sortBy === filters.sortBy && opt.sortOrder === filters.sortOrder
  ) ?? SORT_OPTIONS[0]
  const activeIsView = VIEW_SORTS.includes(activeSort.value)
  const selectSort = useCallback((value: string) => {
    const option = SORT_OPTIONS.find(o => o.value === value)
    if (option) setFilters(prev => ({ ...prev, sortBy: option.sortBy, sortOrder: option.sortOrder }))
  }, [setFilters])

  const hasActiveSearch = filters.query.length > 0
  const hasAnyFilter = activeFilterCount > 0 || hasActiveSearch

  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
    // One atomic reset when the page owns the other filter surface; the
    // per-facet fallback keeps this component usable on its own.
    if (onClearFilters) onClearFilters()
    else {
      onCategoryChange?.('All')
      onPicksOnlyChange?.(false)
    }
    setShowSuggestions(false)
  }, [onClearFilters, onCategoryChange, onPicksOnlyChange])

  return (
    <>
      {/* Sticky filter bar. The <search> landmark IS the sticky box: a
          sticky element can only travel within its parent's box, and when
          the landmark wrapped both this bar and the sort row below, the
          parent was 216px tall and the bar could move 85px, never stick.
          The sort row is not a filter (see below), so it sits outside the
          landmark as a sibling. */}
      <search
        ref={stickyBarRef}
        aria-label="Sound library filters"
        className={`sticky top-[60px] md:top-[64px] z-30 -mx-4 border-b px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 ${className ?? ''}`}
        style={{
          background: 'color-mix(in srgb, var(--tls-canvas) 96%, transparent)',
          borderColor: 'var(--tls-border-subtle)',
        }}
      >
        {/* Search row */}
        <div className="flex items-center gap-2 py-2 md:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-[var(--tls-fg-subtle)]" />
            <Input
              ref={searchInputRef}
              data-search-hotkey-target
              placeholder={isDesktop ? "Search sounds… (press /)" : "Search sounds…"}
              value={filters.query}
              onChange={(e) => handleFilterChange('query', e.target.value)}
              onFocus={() => derivedShowSuggestions && setShowSuggestions(true)}
              onBlur={(e) => {
                const next = e.relatedTarget as Node | null
                if (next && suggestionsContainerRef.current?.contains(next)) return
                setTimeout(() => setShowSuggestions(false), 200)
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' && showSuggestions && derivedShowSuggestions) {
                  e.preventDefault()
                  suggestionItemRefs.current[0]?.focus()
                } else if (e.key === 'Escape' && showSuggestions) {
                  e.preventDefault()
                  setShowSuggestions(false)
                }
              }}
              className="h-[46px] rounded-[var(--ls-radius-0)] border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] pl-9 pr-10 text-[var(--tls-fg-strong)] placeholder:text-[var(--tls-fg-subtle)]"
              aria-label="Search sounds"
            />
            {filters.query && (
              <button type="button"
                onClick={() => handleFilterChange('query', '')}
                className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-[var(--radius-pill)] text-[var(--tls-fg-muted)] transition-colors hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
                aria-label="Clear search"
              >
                <X className="size-3.5" />
              </button>
            )}

            {/* Search Suggestions */}
            {showSuggestions && derivedShowSuggestions && (
              <div
                ref={suggestionsContainerRef}
                className="dropdown-enter dropdown-origin-center absolute z-40 mt-2 w-full overflow-hidden rounded-[var(--ls-radius-0)] border bg-[var(--tls-surface)]"
                style={{
                  borderColor: 'var(--tls-border-subtle)',
                }}
              >
                {searchSuggestions.map((category, index) => (
                  <button type="button"
                    key={category.id || index}
                    ref={(el) => { suggestionItemRefs.current[index] = el }}
                    onMouseDown={() => {
                      handleFilterChange('query', category.name)
                      handleCategorySelect(category.name)
                      setShowSuggestions(false)
                    }}
                    onKeyDown={(e) => handleSuggestionKeyDown(e, index)}
                    onBlur={(e) => {
                      const next = e.relatedTarget as Node | null
                      if (next && suggestionsContainerRef.current?.contains(next)) return
                      setShowSuggestions(false)
                    }}
                    className="w-full border-b px-4 py-2.5 text-left text-sm text-[var(--tls-fg-muted)] transition-colors first:rounded-t-[var(--ls-radius-0)] last:rounded-b-[var(--ls-radius-0)] last:border-b-0 hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
                    style={{ borderColor: 'var(--tls-border-subtle)' }}
                  >
                    <Search className="mr-2 inline size-3 text-[var(--tls-fg-subtle)]" />
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </div>


          {/* Filters: opens the filter sheet below `lg`, toggles the inline
              panel at `lg+`. One button either way, with the applied-facet
              count. */}
          <button
            ref={panelTriggerRef}
            type="button"
            onClick={() => {
              if (isDesktop) setPanelOpen(open => !open)
              else onOpenCategorySheet?.()
            }}
            aria-expanded={isDesktop ? panelOpen : undefined}
            aria-controls={isDesktop ? panelId : undefined}
            className={`tlsr-focus tls-target flex shrink-0 items-center gap-1.5 rounded-[var(--ls-radius-pill)] border px-3 py-2 text-sm font-medium transition-[background-color,border-color,color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] ${
              activeFilterCount > 0 || panelOpen ? activeFilterClassName : neutralFilterClassName
            }`}
          >
            Filters
            {filterCountBadge}
          </button>

        </div>

        {/* Collapsed Filters panel (lg+): model note, editor's picks, categories.
            Below lg the filter sheet holds these. Hidden by display only, so
            the category links stay in the server HTML. */}
        <div
          id={panelId}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return
            e.preventDefault()
            setPanelOpen(false)
            panelTriggerRef.current?.focus()
          }}
          className={`${panelOpen ? 'hidden lg:block' : 'hidden'} border-t py-3`}
          style={{ borderColor: 'var(--tls-border-subtle)' }}
        >
          <div className="flex flex-wrap items-center gap-2 pb-3">
          <p className="min-w-0 flex-1 text-sm leading-snug text-[var(--tls-fg-muted)]">
            Model does not change which sounds you see. {TESLA_FACTS.compatibility.short}{' '}
            <Link prefetch={false} href="/guide" className="tls-text-link tlsr-focus whitespace-nowrap font-semibold">
              Check your car
            </Link>
          </p>
          {onPicksOnlyChange && (
            <button
              type="button"
              role="switch"
              aria-checked={picksOnly}
              onClick={() => onPicksOnlyChange(!picksOnly)}
              className={`flex h-11 items-center gap-1.5 rounded-[var(--ls-radius-pill)] border px-3 py-2 text-sm font-medium transition-[background-color,border-color,color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] ${
                picksOnly ? activeFilterClassName : neutralFilterClassName
              }`}
            >
              <span className="whitespace-nowrap">Editor&apos;s picks</span>
            </button>
          )}
          </div>
          {categories.length > 0 && (
            <div className="max-h-[min(20rem,40vh)] overflow-y-auto">
            <CategoryOverflowRow
              categories={categories}
              categoryCounts={categoryCounts}
              allCategoryCount={allCategoryCount}
              activeCategory={activeCategory}
              onSelectCategory={(categoryName) => {
                handleCategorySelect(categoryName)
                setPanelOpen(false)
              }}
              onClearAll={clearAllFilters}
              hasActiveFilter={hasAnyFilter}
            />
            </div>
          )}
        </div>
      </search>

      {/* List header line: the "Showing 12 of 1,400" position. Outside the
          sticky container; sort moved up into it. `id` is where an active
          ?q= or filter scrolls the page to. */}
      <div
        id={RESULTS_ANCHOR_ID}
        className="mt-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-b pb-2"
        style={{ borderColor: 'var(--tls-border-subtle)' }}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">{resultsSummary}</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <ViewSwitch label="Sort" options={SORT_VIEWS} value={activeSort.value} onChange={selectSort} />
          {/* A native select under a label the width of its text, so the
              fifth control sits on the tabs' line at 390. The select is
              transparent and fills the label; the label draws focus for it. */}
          <span
            className="tls-target relative inline-flex cursor-pointer items-center gap-1 border-b-2 border-solid text-sm font-semibold has-[select:focus-visible]:outline has-[select:focus-visible]:outline-2 has-[select:focus-visible]:outline-offset-2"
            style={{
              borderBottomColor: activeIsView ? 'transparent' : 'var(--tls-brand)',
              color: activeIsView ? 'var(--tls-fg-muted)' : 'var(--tls-fg-strong)',
              outlineColor: 'var(--tls-brand)',
            }}
          >
            {activeIsView ? 'More' : activeSort.short}
            <ChevronDown className="size-3.5" aria-hidden="true" />
            <select
              aria-label="More sorts"
              value={activeIsView ? '' : activeSort.value}
              onChange={(e) => selectSort(e.target.value)}
              className="absolute inset-0 cursor-pointer opacity-0"
            >
              <option value="" disabled>More</option>
              {SORT_OPTIONS.filter(opt => !VIEW_SORTS.includes(opt.value)).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </span>
        </div>
      </div>
    </>
  )
}

export default SearchFilters
